import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Label } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import notify, { MESSAGES } from '../../../utils/notify';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import { fetchHpsReviseThread, postHpsRevise } from '../utils/hpsRevise';

const COPY = {
  Evaluator: {
    title: 'Correction',
    label: 'Message',
    placeholder:
      'Explain what should be fixed. Example: parcel 2 does not match the released HPS document.',
    send: 'Send to Revise',
    hint: 'Tell HPS what should be fixed.',
    statusHPS2: 2,
    notesRequired: true,
  },
  Hps: {
    title: 'Correction',
    label: 'Reply',
    placeholder:
      'Add a short reply if you want. A summary of the change is included for you.',
    send: 'Send reply',
    hint: 'Reply after the data has been updated.',
    statusHPS2: 1,
    notesRequired: false,
  },
};

const HpsRevisePanel = ({
  procurementId,
  role,
  canSend = false,
  expandToken = 0,
  onSent,
  statusKind = null,
  alwaysVisible = false,
  stickToBottom = true,
}) => {
  const copy = COPY[role] ?? COPY.Evaluator;
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (!procurementId) return;
    try {
      const next = await fetchHpsReviseThread(procurementId);
      setItems(next);
      if (next.length) setExpanded(true);
    } catch (error) {
      notify.apiError(error, 'load HPS revision');
    } finally {
      setLoaded(true);
    }
  }, [procurementId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!expandToken) return;
    setExpanded(true);
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [expandToken]);

  const handleSend = async () => {
    const respondNotes = notes.trim();
    if (copy.notesRequired && !respondNotes) {
      notify.warning('Write a message before sending.');
      return;
    }

    setIsSaving(true);
    try {
      await postHpsRevise({
        procurementId,
        role,
        respondNotes,
        statusHPS2: copy.statusHPS2,
      });
      notify.success(MESSAGES.updated);
      setNotes('');
      setExpanded(true);
      await load();
      onSent?.();
    } catch (error) {
      notify.apiError(error, 'send HPS revision');
    } finally {
      setIsSaving(false);
    }
  };

  if (!loaded) return null;
  if (role === 'Hps' && !items.length) return null;
  if (!alwaysVisible && !canSend && !items.length && !expandToken) return null;

  const lastRole = items[items.length - 1]?.role;
  const updated =
    items.length > 0 &&
    (statusKind ? statusKind === 'available' : lastRole === 'Hps');
  const tone = expanded
    ? 'open'
    : !items.length
      ? 'idle'
      : updated
        ? 'done'
        : 'wait';
  const statusLabel = !items.length
    ? 'No request yet'
    : updated
      ? 'Updated'
      : 'Not updated yet';
  const statusLine = !items.length
    ? 'No correction has been sent.'
    : updated
      ? 'HPS has updated the data.'
      : 'HPS has not updated the data yet.';
  const headClass = {
    open: 'bg-primary text-white',
    done: 'bg-success text-white',
    wait: 'bg-warning text-dark',
    idle: 'bg-primary-subtle text-primary',
  }[tone];
  const frame = {
    open: {
      borderColor: 'var(--vz-primary)',
      boxShadow: '0 0 0 3px rgba(var(--vz-primary-rgb), 0.2)',
    },
    done: {
      borderColor: 'var(--vz-success)',
      boxShadow: '0 0 0 3px rgba(var(--vz-success-rgb), 0.2)',
    },
    wait: {
      borderColor: 'var(--vz-warning)',
      boxShadow: '0 0 0 3px rgba(var(--vz-warning-rgb), 0.35)',
    },
    idle: { borderColor: 'var(--vz-primary)' },
  }[tone];

  return (
    <div
      ref={panelRef}
      className={
        stickToBottom
          ? 'req-actionbar flex-column align-items-stretch'
          : 'd-flex flex-column align-items-stretch'
      }
      style={frame}
    >
      <button
        type='button'
        className={`btn d-flex align-items-center gap-2 text-decoration-none w-100 text-start rounded px-3 py-2 ${headClass}`}
        onClick={() => setExpanded(prev => !prev)}
      >
        <span
          className='rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 bg-white text-primary'
          style={{ width: '28px', height: '28px' }}
        >
          <i
            className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line fs-16`}
          ></i>
        </span>
        <span className='d-flex flex-column flex-grow-1'>
          <span className='fw-semibold fs-13'>
            {items.length ? `${copy.title} (${items.length})` : copy.title}
          </span>
          <span className={`small ${expanded ? 'opacity-75' : ''}`}>
            {expanded ? copy.hint : statusLine}
          </span>
        </span>
        <span className='badge bg-white text-dark flex-shrink-0'>
          {statusLabel}
        </span>
      </button>

      {expanded ? (
        <div className='mt-3 d-flex flex-column gap-2 bg-primary-subtle rounded-3 p-3'>
          {items.length ? (
            items.map((item, index) => (
              <div
                key={item.id ?? index}
                className={`border rounded-3 p-2 small ${
                  item.role === 'Evaluator' ? 'bg-light' : 'bg-white'
                }`}
              >
                <div className='d-flex justify-content-between gap-2 text-muted fs-12'>
                  <span className='fw-semibold text-dark'>
                    {item.role === 'Evaluator' ? 'Evaluator' : 'HPS'}
                    {item.createdByLabel ? ` · ${item.createdByLabel}` : ''}
                  </span>
                  <span>{formatDateToddmmyyhhmm(item.createdDate)}</span>
                </div>
                {item.respondNotes ? (
                  <p className='mb-0 mt-1 text-dark'>{item.respondNotes}</p>
                ) : null}
                {item.systemNote ? (
                  <p className='mb-0 mt-1 text-muted'>{item.systemNote}</p>
                ) : null}
              </div>
            ))
          ) : (
            <p className='text-muted small mb-0'>No messages yet.</p>
          )}
          {canSend ? (
            <div>
              <Label className='form-label fw-semibold text-dark fs-13 mb-1'>
                {copy.label}
              </Label>
              <Input
                type='textarea'
                rows={3}
                value={notes}
                placeholder={copy.placeholder}
                disabled={isSaving}
                onChange={event => setNotes(event.target.value)}
              />
              <div className='d-flex justify-content-end mt-2'>
                <ActionButton
                  variant='primary'
                  size='sm'
                  icon='ri-send-plane-line'
                  loading={isSaving}
                  onClick={handleSend}
                >
                  {copy.send}
                </ActionButton>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default HpsRevisePanel;
